import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Calendar, Eye, Users, DollarSign, Edit, Send, BarChart3, Shield, List, HelpCircle, Wallet } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import AnalyticsCharts from '../components/AnalyticsCharts';
import TwoFactorAuth from '../components/TwoFactorAuth';
import OrganizerOnboarding from '../components/OrganizerOnboarding';
import OrganizerFinances from '../components/OrganizerFinances';
import { PAYMENTS_ENABLED } from '../config/features';
import CreateEventForm from '../components/CreateEventForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['Arte', 'Cultura', 'Deporte', 'Educación', 'Gastronomía', 'Música', 'Solidario', 'Tecnología', 'Turismo'];

const OrganizerDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'organizer' && user.role !== 'admin') {
      toast.error('Necesitás ser organizador para acceder');
      navigate('/');
      return;
    }
    loadEvents();
    // Check if onboarding should be shown
    const completed = localStorage.getItem(`onboarding_organizer_${user.user_id}`);
    if (!completed) {
      // Delay slightly so the dashboard renders first
      setTimeout(() => setShowOnboarding(true), 600);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  const handleCloseOnboarding = (completed) => {
    if (completed && user) {
      localStorage.setItem(`onboarding_organizer_${user.user_id}`, 'true');
    }
    setShowOnboarding(false);
  };

  const reopenOnboarding = () => {
    setShowOnboarding(true);
  };

  const loadEvents = async () => {
    try {
      const response = await axios.get(`${API}/organizer/events`, {
        withCredentials: true
      });
      setEvents(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/submit`, {}, {
        withCredentials: true
      });
      toast.success('Evento enviado a revisión');
      loadEvents();
    } catch (error) {
      toast.error('Error al enviar evento');
    }
  };

  const statusLabels = {
    draft: { label: 'Borrador', color: 'bg-[#F5F5F5] text-[#525252]' },
    review: { label: 'En revisión', color: 'bg-yellow-500/10 text-yellow-400' },
    published: { label: 'Publicado', color: 'bg-green-500/10 text-green-400' },
    paused: { label: 'Pausado', color: 'bg-orange-500/10 text-orange-400' },
    cancelled: { label: 'Cancelado', color: 'bg-red-500/10 text-red-400' },
    finished: { label: 'Finalizado', color: 'bg-[#F5F5F5] text-[#737373]' }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background py-8" data-testid="organizer-dashboard">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-heading font-bold text-[#0A0A0A]">Panel Organizador</h1>
            <p className="text-[#737373] mt-2">Gestioná tus eventos y veé estadísticas</p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-primary hover:bg-primary/90 text-white"
            data-testid="create-event-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Crear evento
          </Button>
        </div>

        {/* Help button - reopen tutorial */}
        <div className="mb-4">
          <button
            onClick={reopenOnboarding}
            className="text-sm text-[#525252] hover:text-primary inline-flex items-center gap-1 transition-colors"
            data-testid="reopen-onboarding-button"
          >
            <HelpCircle className="w-4 h-4" />
            ¿Cómo funciona el panel? Ver tutorial
          </button>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white/50 border-[#E5E5E5]">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#737373]">Total eventos</p>
                  <p className="text-3xl font-bold text-[#0A0A0A]" data-testid="stat-total-events">{events.length}</p>
                </div>
                <Calendar className="w-10 h-10 text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/50 border-[#E5E5E5]">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#737373]">Vistas totales</p>
                  <p className="text-3xl font-bold text-[#0A0A0A]" data-testid="stat-total-views">
                    {events.reduce((sum, e) => sum + (e.views || 0), 0)}
                  </p>
                </div>
                <Eye className="w-10 h-10 text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/50 border-[#E5E5E5]">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#737373]">RSVPs totales</p>
                  <p className="text-3xl font-bold text-[#0A0A0A]" data-testid="stat-total-rsvps">
                    {events.reduce((sum, e) => sum + (e.rsvp_count || 0), 0)}
                  </p>
                </div>
                <Users className="w-10 h-10 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs: Events / Analytics / Security */}
        <Tabs defaultValue="events" className="w-full">
          <TabsList className="bg-white border-[#E5E5E5]">
            <TabsTrigger value="events" data-testid="tab-my-events">
              <List className="w-4 h-4 mr-2" /> Mis Eventos
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <BarChart3 className="w-4 h-4 mr-2" /> Analytics
            </TabsTrigger>
            {PAYMENTS_ENABLED && (
              <TabsTrigger value="finances" data-testid="tab-finances">
                <Wallet className="w-4 h-4 mr-2" /> Finanzas
              </TabsTrigger>
            )}
            <TabsTrigger value="security" data-testid="tab-security">
              <Shield className="w-4 h-4 mr-2" /> Seguridad
            </TabsTrigger>
          </TabsList>

          <TabsContent value="events">
            <Card className="bg-white/50 border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-2xl text-[#0A0A0A]">Mis Eventos</CardTitle>
              </CardHeader>
              <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => <div key={i} className="animate-pulse bg-[#F5F5F5] h-24 rounded-lg" />)}
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-[#737373] mx-auto mb-4" />
                <p className="text-[#737373] mb-4">No tenés eventos creados aún</p>
                <Button onClick={() => setShowForm(true)} className="bg-primary">Crear primer evento</Button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="events-list">
                {events.map(event => {
                  const firstMedia = event.video_url || (event.images && event.images[0]);
                  const hasVideo = !!event.video_url;
                  const thumbSrc = firstMedia
                    ? (firstMedia.startsWith('http') ? firstMedia : `${API}/files/${firstMedia}`)
                    : null;
                  return (
                  <div key={event.event_id} className="bg-white/50 border border-[#E5E5E5] rounded-lg p-4 hover:border-primary transition-colors" data-testid={`event-${event.event_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      {/* Thumbnail */}
                      <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 rounded-md overflow-hidden bg-[#F5F5F5] border border-[#E5E5E5] relative">
                        {thumbSrc ? (
                          hasVideo ? (
                            <div className="w-full h-full bg-black flex items-center justify-center relative">
                              <video src={thumbSrc} className="w-full h-full object-cover opacity-70" muted preload="metadata" />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center">
                                  <Send className="w-3 h-3 text-black rotate-90" fill="currentColor" />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <img src={thumbSrc} alt="" className="w-full h-full object-cover" />
                          )
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Calendar className="w-8 h-8 text-[#B0B0B0]" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${statusLabels[event.status].color}`}>
                            {statusLabels[event.status].label}
                          </span>
                          <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">{event.category}</span>
                        </div>
                        <h3 className="text-lg font-semibold text-[#0A0A0A] mb-1 truncate">{event.title}</h3>
                        <p className="text-sm text-[#737373] mb-3 line-clamp-2">{event.description_short}</p>
                        <div className="flex items-center gap-4 text-xs text-[#737373] flex-wrap">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" /> {event.views || 0} vistas
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" /> {event.rsvp_count || 0} RSVPs
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {format(new Date(event.start_date), "d MMM, yyyy", { locale: es })}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <Link to={`/eventos/${event.event_id}`}>
                          <Button size="sm" variant="outline" className="border-[#E5E5E5] w-full">Ver</Button>
                        </Link>
                        {event.status === 'draft' && (
                          <Button
                            onClick={() => handleSubmit(event.event_id)}
                            size="sm"
                            className="bg-primary hover:bg-primary/90"
                            data-testid={`submit-${event.event_id}`}
                          >
                            <Send className="w-3 h-3 mr-1" /> Enviar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsCharts />
          </TabsContent>

          {PAYMENTS_ENABLED && (
            <TabsContent value="finances">
              <OrganizerFinances />
            </TabsContent>
          )}

          <TabsContent value="security">
            <TwoFactorAuth />
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Event Form Modal */}
      {showForm && (
        <CreateEventForm
          onClose={() => setShowForm(false)}
          onSuccess={() => { setShowForm(false); loadEvents(); }}
        />
      )}

      {/* Onboarding Tutorial */}
      <OrganizerOnboarding
        open={showOnboarding}
        onClose={handleCloseOnboarding}
      />
    </div>
  );
};


export default OrganizerDashboard;
